//employeeonboarding

import { useState, useEffect } from "react";
import { auth, db, storage } from "../firebase/config";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { PDFDocument } from "pdf-lib";

export default function EmployeeOnboarding() {
  const [categories, setCategories] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTaskIndex, setSelectedTaskIndex] = useState(0);
  const [taskInputs, setTaskInputs] = useState({});
  const [isAllTasksCompleted, setIsAllTasksCompleted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfInputs, setPdfInputs] = useState({});
  const [showPdfInputModal, setShowPdfInputModal] = useState(false);
  const [modifiedPdfUrl, setModifiedPdfUrl] = useState("");

  console.log(modifiedPdfUrl);
  console.log(pdfInputs);

  //const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          setLoading(true);
          setError(""); // ✅ Clear previous errors

          // ✅ Search for the employee's company ID
          const companiesRef = collection(db, "companies");
          const companiesSnapshot = await getDocs(companiesRef);
          let foundCompanyId = null;
          let foundEmployeeDoc = null;

          for (const companyDoc of companiesSnapshot.docs) {
            const employeesRef = collection(
              db,
              `companies/${companyDoc.id}/employees`
            );
            const employeeDocRef = doc(employeesRef, user.uid);
            const employeeDoc = await getDoc(employeeDocRef);
            if (employeeDoc.exists()) {
              foundCompanyId = companyDoc.id;
              foundEmployeeDoc = employeeDoc;
              break;
            }
          }

          if (!foundCompanyId || !foundEmployeeDoc) {
            throw new Error("Company ID not found.");
          }

          setCompanyId(foundCompanyId);
          setEmployeeId(user.uid);
        } catch (err) {
          console.error("Error fetching employee data:", err.message);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      } else {
        setError("Employee not authenticated.");
        setLoading(false);
      }
    });

    return () => unsubscribe(); // ✅ Cleanup listener on unmount
  }, []);

  const checkIfAllTasksCompleted = () => {
    const allTasksCompleted = categories.every((category) =>
      category.tasks.every((task) => task.status === "completed")
    );

    setIsAllTasksCompleted(allTasksCompleted);
  };

  useEffect(() => {
    if (!companyId || !employeeId) return;

    const fetchCategories = async () => {
      try {
        setLoading(true);
        console.log("Fetching categories...");

        const categoriesRef = collection(
          db,
          `companies/${companyId}/employees/${employeeId}/onboarding_progress`
        );
        const categoriesSnapshot = await getDocs(categoriesRef);
        const categoryData = categoriesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        console.log("Fetched category data:", categoryData);

        setCategories(categoryData);

        // ✅ Set pdfUrl for the selected task ONLY
        if (categoryData.length > 0) {
          const selectedTask = categoryData[0]?.tasks?.[selectedTaskIndex]; // Dynamically get the selected task

          console.log("Selected Task:", selectedTask);

          if (selectedTask && selectedTask.fileUrl) {
            console.log(
              "✅ Setting PDF URL for selected task:",
              selectedTask.fileUrl
            );
            setPdfUrl(selectedTask.fileUrl);
          } else {
            console.warn("❌ No file URL found for selected task");
            setPdfUrl(""); // Clear PDF URL when switching to a task without a file
          }
        }
      } catch (err) {
        console.error("❌ Error fetching tasks:", err);
        setError("Failed to fetch tasks.");
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [companyId, employeeId, selectedTaskIndex]); // ✅ Added selectedTaskIndex to track when switching tasks

  useEffect(() => {
    console.log("Updated PDF URL:", pdfUrl);
  }, [pdfUrl]);

  useEffect(() => {
    if (!pdfUrl) {
      setModifiedPdfUrl(""); // ✅ Clear the modified PDF URL when there's no file for the task
      return;
    }

    const loadAndModifyPdf = async () => {
      try {
        console.log("Fetching PDF from:", pdfUrl);
        const response = await fetch(pdfUrl);
        if (!response.ok) throw new Error("Failed to fetch PDF");

        const arrayBuffer = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);

        const form = pdfDoc.getForm();
        const fields = form.getFields();

        console.log(
          "📄 Available fields:",
          fields.map((f) => f.getName())
        );

        const extractedInputs = {};
        fields.forEach((field) => {
          const fieldName = field.getName();
          const fieldValue = field.getText();
          extractedInputs[fieldName] = fieldValue;
        });

        setPdfInputs(extractedInputs);
        setShowPdfInputModal(true); // Show input UI for manual entry
        console.log("📥 PDF Inputs Updated:", extractedInputs);

        const modifiedPdfBytes = await pdfDoc.save();

        const modifiedBlob = new Blob([modifiedPdfBytes], {
          type: "application/pdf",
        });
        const modifiedUrl = URL.createObjectURL(modifiedBlob);
        console.log("✅ Modified PDF URL:", modifiedUrl);

        setModifiedPdfUrl(modifiedUrl); // ✅ Store modified PDF in separate state
      } catch (err) {
        console.error("❌ Error modifying PDF:", err);
        setError("Failed to modify PDF.");
      }
    };

    loadAndModifyPdf();
  }, [pdfUrl]); //

  useEffect(() => {
    console.log("🔄 Updated pdfInput State:", pdfInputs);
  }, [pdfInputs]);

  // ✅ Function to handle input changes in the modal
  const handlePdfInputChange = (field, value) => {
    setPdfInputs((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ Function to apply input values and close modal
  // ✅ Function to apply input values, modify PDF, and close modal
  const handleSavePdfInputs = async () => {
    try {
      console.log("Applying inputs to PDF:", pdfInputs);
      setShowPdfInputModal(false);

      const response = await fetch(pdfUrl);
      const arrayBuffer = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      const form = pdfDoc.getForm();
      const fields = form.getFields();

      fields.forEach((field) => {
        const fieldName = field.getName();
        if (field.setText) {
          const value = pdfInputs[fieldName] || "";
          field.setText(value);
          console.log(`✅ Applied '${value}' to field '${fieldName}'`);
        }
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const modifiedBlob = new Blob([modifiedPdfBytes], {
        type: "application/pdf",
      });
      const modifiedUrl = URL.createObjectURL(modifiedBlob);
      setModifiedPdfUrl(modifiedUrl);
      console.log("✅ Updated PDF saved successfully!");
    } catch (err) {
      console.error("❌ Error applying inputs to PDF:", err);
    }
  };

  useEffect(() => {
    if (categories.length > 0) {
      const allTasksCompleted = categories.every((category) =>
        category.tasks.every((task) => task.status === "completed")
      );

      setIsAllTasksCompleted(allTasksCompleted);
    }
  }, [categories]);

  const handleInputChange = (taskId, field, value) => {
    setTaskInputs((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], [field]: value },
    }));
  };

  const handleCompleteTask = async (categoryId, taskId, updatedFields) => {
    const confirmSubmission = window.confirm(
      "Are you sure you have reviewed the fields before completing the task?"
    );

    if (!confirmSubmission) {
      return;
    }

    // Retrieve the task type
    const selectedTask = categories
      .flatMap((category) => category.tasks)
      .find((task) => task.taskId === taskId);

    if (!selectedTask) {
      alert("Task not found.");
      return;
    }

    // Only check for modifiedPdfUrl if it's a document task
    if (selectedTask.taskType === "document_task" && !modifiedPdfUrl) {
      alert(
        "The modified document is not available yet. Please wait and try again."
      );
      return;
    }

    try {
      let uploadedPdfUrl = null; // Default value for non-document tasks

      if (selectedTask.taskType === "document_task") {
        // Upload modified PDF if it's a document task
        const fileName = `onboarding_docs/${employeeId}_${taskId}.pdf`;
        const storageRef = ref(storage, fileName);

        // Convert Blob URL to File
        const response = await fetch(modifiedPdfUrl);
        const pdfBlob = await response.blob();

        // Upload Blob to Firebase Storage
        await uploadBytes(storageRef, pdfBlob);

        // Get the final download URL
        uploadedPdfUrl = await getDownloadURL(storageRef);
        console.log("✅ Uploaded PDF URL:", uploadedPdfUrl);
      }

      // Save to Firestore
      const categoryRef = doc(
        db,
        `companies/${companyId}/employees/${employeeId}/onboarding_progress/${categoryId}`
      );

      const categoryDoc = await getDoc(categoryRef);
      if (!categoryDoc.exists()) {
        alert("Category not found.");
        return;
      }

      let categoryData = categoryDoc.data();

      // Update only the completed task with assigned fields
      const updatedTasks = categoryData.tasks.map((task) =>
        task.taskId === taskId
          ? {
              ...task,
              status: "completed",
              fileUrl: uploadedPdfUrl, // Only updated if it's a document task
              assignedFields: updatedFields, // Store entered values
              completedAt: new Date().toISOString(),
            }
          : task
      );

      console.log("📄 Updated Tasks with Final Data:", updatedTasks);

      await updateDoc(categoryRef, { tasks: updatedTasks });

      alert("Task completed successfully. The employer can now review it.");
    } catch (err) {
      console.error("❌ Error updating task:", err.message);
      setError("Failed to complete task.");
    }
  };

  const handleResubmitTask = async (categoryId, taskId, updatedFields) => {
    const confirmResubmission = window.confirm(
      "Are you sure you want to resubmit this task? Please review the fields before resubmission."
    );

    if (!confirmResubmission) {
      return; // Stop the submission if the user cancels
    }

    // Retrieve the task type
    const selectedTask = categories
      .flatMap((category) => category.tasks)
      .find((task) => task.taskId === taskId);

    if (!selectedTask) {
      alert("Task not found.");
      return;
    }

    // Only check for modifiedPdfUrl if it's a document task
    if (selectedTask.taskType === "document_task" && !modifiedPdfUrl) {
      alert(
        "The modified document is not available yet. Please wait and try again."
      );
      return;
    }

    try {
      let uploadedPdfUrl = null; // Default value for non-document tasks

      if (selectedTask.taskType === "document_task") {
        // Upload modified PDF if it's a document task
        const fileName = `onboarding_docs/${employeeId}_${taskId}_resubmitted.pdf`;
        const storageRef = ref(storage, fileName);

        // Convert Blob URL to File
        const response = await fetch(modifiedPdfUrl);
        const pdfBlob = await response.blob();

        // Upload Blob to Firebase Storage
        await uploadBytes(storageRef, pdfBlob);

        // Get the final download URL
        uploadedPdfUrl = await getDownloadURL(storageRef);
        console.log("✅ Uploaded Resubmitted PDF URL:", uploadedPdfUrl);
      }

      // Save to Firestore
      const categoryRef = doc(
        db,
        `companies/${companyId}/employees/${employeeId}/onboarding_progress/${categoryId}`
      );

      const categoryDoc = await getDoc(categoryRef);
      if (!categoryDoc.exists()) {
        alert("Category not found.");
        return;
      }

      let categoryData = categoryDoc.data();

      // Update only the resubmitted task with assigned fields
      const updatedTasks = categoryData.tasks.map((task) =>
        task.taskId === taskId
          ? {
              ...task,
              status: "completed", // ✅ Auto-mark as completed
              reviewStatus: "pending", // ✅ Reset review status for employer
              fileUrl: uploadedPdfUrl, // ✅ Only update if it's a document task
              assignedFields: updatedFields, // ✅ Store updated user input
              completedAt: new Date().toISOString(), // ✅ Log new completion time
            }
          : task
      );

      console.log("📄 Updated Tasks with Final Data:", updatedTasks);

      await updateDoc(categoryRef, { tasks: updatedTasks });

      // ✅ Update UI immediately
      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === categoryId ? { ...cat, tasks: updatedTasks } : cat
        )
      );
      checkIfAllTasksCompleted();

      alert("Task resubmitted successfully. The employer can now review it.");
    } catch (err) {
      console.error("❌ Error resubmitting task:", err.message);
      setError("Failed to resubmit task.");
    }
  };

  const handleTaskNavigation = (index) => {
    setSelectedTaskIndex(index);
  };

  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        height: "100vh",
        border: "1px solid #ccc",
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          width: "20%",
          backgroundColor: "#ddd",
          padding: "10px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {categories.length > 0 &&
          categories[0].tasks.map((task, index) => (
            <button
              key={task.taskId}
              onClick={() => handleTaskNavigation(index)}
              style={{
                width: "90%",
                padding: "10px",
                margin: "5px 0",
                backgroundColor: selectedTaskIndex === index ? "green" : "#bbb",
                color: "white",
                border: "none",
                cursor: "pointer",
                borderRadius: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              Task {index + 1}
              {selectedTaskIndex === index && (
                <span
                  style={{
                    width: "10px",
                    height: "10px",
                    backgroundColor: "white",
                    borderRadius: "50%",
                  }}
                ></span>
              )}
            </button>
          ))}
      </div>

      {/* Main Content */}
      <div
        style={{
          width: "80%",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <h1>Employee Onboarding Form</h1>

        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p style={{ color: "red" }}>{error}</p>
        ) : isAllTasksCompleted ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
              textAlign: "center",
            }}
          >
            <h1>✅ Thank You for Your Submission!</h1>
            <p>Your onboarding documents are being reviewed.</p>
          </div>
        ) : categories.length === 0 ? (
          <p>No onboarding tasks assigned.</p>
        ) : (
          <div
            style={{
              width: "60%",
              border: "1px solid #ccc",
              padding: "20px",
              borderRadius: "10px",
              backgroundColor: "#f9f9f9",
              boxShadow: "2px 2px 10px rgba(0,0,0,0.1)",
            }}
          >
            <h3 style={{ textAlign: "center", fontWeight: "bold" }}>
              Personal Information
            </h3>
            {/* Display PDF only if the selected task has a file */}
            {pdfUrl && modifiedPdfUrl && (
              <div
                style={{
                  marginBottom: "10px",
                  border: "1px solid #ccc",
                  padding: "10px",
                }}
              >
                <h3>📄 Modified Document</h3>
                <iframe
                  title="Modified PDF Document"
                  src={modifiedPdfUrl}
                  width="100%"
                  height="500px"
                  style={{ border: "none" }}
                />
              </div>
            )}

            {modifiedPdfUrl && (
              <a href={modifiedPdfUrl} download="ModifiedPDF.pdf">
                📥 Download Modified PDF
              </a>
            )}

            {pdfUrl && (
              <a href={pdfUrl} download="PDF.pdf">
                📥 Download old PDF
              </a>
            )}

            {showPdfInputModal && (
              <div
                style={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "#fff",
                  padding: "20px",
                  boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                  borderRadius: "10px",
                  width: "300px",
                  zIndex: 1000,
                }}
              >
                <h2>Enter Field Values</h2>
                {Object.keys(pdfInputs).map((field, index) => (
                  <div
                    key={index}
                    style={{ marginBottom: "10px", textAlign: "left" }}
                  >
                    <label style={{ fontWeight: "bold" }}>{field}</label>
                    <input
                      type="text"
                      value={pdfInputs[field]}
                      onChange={(e) =>
                        handlePdfInputChange(field, e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "5px",
                      }}
                    />
                  </div>
                ))}
                <button
                  onClick={handleSavePdfInputs}
                  style={{
                    backgroundColor: "black",
                    color: "white",
                    padding: "10px",
                    width: "100%",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
              </div>
            )}

            {/* Selected Task Info */}
            {categories[0].tasks[selectedTaskIndex] && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const updatedFields = {};
                  Object.keys(
                    categories[0].tasks[selectedTaskIndex].assignedFields
                  ).forEach((field) => {
                    updatedFields[field] = e.target[field].value;
                  });

                  if (
                    categories[0].tasks[selectedTaskIndex].reviewStatus ===
                    "rejected"
                  ) {
                    handleResubmitTask(
                      categories[0].id,
                      categories[0].tasks[selectedTaskIndex].taskId,
                      updatedFields
                    );
                  } else {
                    handleCompleteTask(
                      categories[0].id,
                      categories[0].tasks[selectedTaskIndex].taskId,
                      updatedFields
                    );
                  }
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {categories[0].tasks[selectedTaskIndex].fileUrl && (
                  <div style={{ marginBottom: "10px" }}>
                    <p>📄 This task requires you to review a document.</p>
                  </div>
                )}

                {Object.keys(
                  categories[0].tasks[selectedTaskIndex].assignedFields
                ).map((field, idx) => (
                  <div
                    key={idx}
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <label style={{ fontWeight: "bold" }}>{field}</label>
                    <input
                      type="text"
                      name={field}
                      value={
                        taskInputs[
                          categories[0].tasks[selectedTaskIndex].taskId
                        ]?.[field] || ""
                      }
                      onChange={(e) =>
                        handleInputChange(
                          categories[0].tasks[selectedTaskIndex].taskId,
                          field,
                          e.target.value
                        )
                      }
                      required
                      style={{
                        padding: "8px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        backgroundColor:
                          categories[0].tasks[selectedTaskIndex].status ===
                          "completed"
                            ? "#e0e0e0"
                            : "white",
                      }}
                      disabled={
                        categories[0].tasks[selectedTaskIndex].status ===
                        "completed"
                      }
                    />
                  </div>
                ))}

                {/* Buttons */}
                <button
                  type="submit"
                  style={{
                    marginTop: "10px",
                    padding: "10px",
                    border: "none",
                    cursor: "pointer",
                    borderRadius: "5px",
                    width: "100%",
                    fontWeight: "bold",
                    backgroundColor:
                      categories[0].tasks[selectedTaskIndex].reviewStatus ===
                      "rejected"
                        ? "#007bff"
                        : "black",
                    color: "white",
                  }}
                  disabled={
                    categories[0].tasks[selectedTaskIndex].status ===
                    "completed"
                  }
                >
                  {categories[0].tasks[selectedTaskIndex].reviewStatus ===
                  "rejected"
                    ? "🔄 Resubmit Task"
                    : "✔ Complete"}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
